// ─────────────────────────────────────────────────────────────────────────────
// SessionCsvLogger.js — On-water validation logger for Mode 2 (Phone GPS).
//
// Three-button compact strip:
//   • Start Log / Stop Log — toggle. Pushes a row to an in-memory array every
//     1 s containing { elapsedTime, distance, pace, spm, lat, lng, accuracy }.
//   • Save CSV             — serialises the in-memory rows to a CSV blob and
//     triggers a standard <a download> click so the phone's browser saves to
//     Downloads (or Documents on iOS via Save File).
//
// Independent of the workout session timer — start/stop affect only the CSV
// buffer. Re-tapping Start Log while already logging is a no-op (per spec,
// must not reset). Saving with an empty buffer surfaces the inline
// "No data to save." message.
//
// Receives a `getRow()` function from the parent — called once per second —
// so this component does not need to subscribe to context (avoids extra
// re-renders of the parent every tick).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { Circle, Square, Download } from 'lucide-react';

const CSV_HEADER = 'Elapsed Time (s),Distance (m),Pace (MM:SS),Stroke Rate (SPM),Latitude,Longitude,GPS Accuracy (m)';

// Escape a CSV cell — wraps in quotes if it contains a comma/quote/newline.
function csvCell(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToCsv(r) {
  return [
    Math.round(r.elapsedTime ?? 0),
    Math.round(r.distance ?? 0),
    r.pace ?? '',
    r.spm ?? '',
    r.lat != null ? r.lat.toFixed(7) : '',
    r.lng != null ? r.lng.toFixed(7) : '',
    r.accuracy != null ? r.accuracy.toFixed(1) : '',
  ].map(csvCell).join(',');
}

function tsForFilename(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_` +
    `${pad(d.getHours())}-${pad(d.getMinutes())}`
  );
}

/**
 * @param {object} props
 * @param {() => object} props.getRow — function returning the live snapshot:
 *        { elapsedTime, distance, pace, spm, lat, lng, accuracy }
 */
export default function SessionCsvLogger({ getRow }) {
  const [logging, setLogging] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [hint, setHint] = useState(null);
  const rowsRef = useRef([]);
  const intervalRef = useRef(null);
  // Latest getRow without re-binding the interval each render.
  const getRowRef = useRef(getRow);
  useEffect(() => { getRowRef.current = getRow; }, [getRow]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const flashHint = (text, ms = 2500) => {
    setHint(text);
    setTimeout(() => setHint((cur) => (cur === text ? null : cur)), ms);
  };

  const startLog = () => {
    if (logging) return;            // ignore tap while already logging
    setLogging(true);
    setHint(null);
    intervalRef.current = setInterval(() => {
      try {
        const snap = getRowRef.current?.();
        if (!snap) return;
        rowsRef.current.push(snap);
        setRowCount(rowsRef.current.length);
      } catch (_e) { /* swallow — never crash the monitor */ }
    }, 1000);
  };

  const stopLog = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setLogging(false);
  };

  const toggleLog = () => (logging ? stopLog() : startLog());

  const saveCsv = () => {
    if (rowsRef.current.length === 0) {
      flashHint('No data to save.');
      return;
    }
    const lines = [CSV_HEADER, ...rowsRef.current.map(rowToCsv)];
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv' });
    const filename = `CrewZ_session_${tsForFilename()}.csv`;
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      flashHint(`Saved ${filename} (${rowsRef.current.length} rows)`, 4000);
    } catch (_e) {
      flashHint('Save failed — try a desktop browser.');
    }
  };

  // Reset the buffer + counters. Hidden behind a long-ish label so it's not
  // tapped by accident; appears only when there's data to clear.
  const resetBuffer = () => {
    if (logging) return;
    rowsRef.current = [];
    setRowCount(0);
    flashHint('Log cleared.');
  };

  return (
    <div
      data-testid="session-csv-logger"
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-wider uppercase text-zinc-500 dark:text-zinc-400">
            Session Log
          </span>
          <span
            data-testid="session-csv-row-count"
            className="text-[11px] text-zinc-500 dark:text-zinc-400 font-mono"
          >
            {rowCount} row{rowCount === 1 ? '' : 's'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLog}
            data-testid="session-csv-toggle-btn"
            className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider uppercase flex items-center gap-1.5 transition-colors ${
              logging
                ? 'bg-rose-500 hover:bg-rose-400 text-white'
                : 'bg-zinc-900 hover:bg-zinc-700 dark:bg-zinc-100 dark:hover:bg-white text-white dark:text-zinc-900'
            }`}
            title={logging ? 'Stop logging — keeps data in memory' : 'Begin a new session log (1Hz)'}
          >
            {logging ? (
              <>
                <Circle className="w-3 h-3 fill-white animate-pulse" />
                Stop Log
              </>
            ) : (
              <>
                <Square className="w-3 h-3" />
                Start Log
              </>
            )}
          </button>

          <button
            onClick={saveCsv}
            data-testid="session-csv-save-btn"
            disabled={logging}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider uppercase flex items-center gap-1.5 transition-colors ${
              logging
                ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
            title={logging ? 'Stop logging before saving' : 'Save CSV to your phone'}
          >
            <Download className="w-3 h-3" />
            Save CSV
          </button>

          {rowCount > 0 && !logging && (
            <button
              onClick={resetBuffer}
              data-testid="session-csv-reset-btn"
              className="px-2 py-1.5 rounded-lg text-[10px] font-bold tracking-wider uppercase text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200"
              title="Discard buffered rows"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {hint && (
        <div
          data-testid="session-csv-hint"
          className="mt-2 text-xs text-zinc-600 dark:text-zinc-400"
        >
          {hint}
        </div>
      )}
    </div>
  );
}
